import type {
    IsoAndUnixTimestamp} from "@@/shared/date-time/iso-and-unix";
import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
} from "@@/shared/date-time/iso-and-unix";
import { idZodSchema } from "@@/shared/id";
import type { Price} from "@@/shared/prices/price";
import { priceZodSchema } from "@@/shared/prices/price";
import type {
    ProviderId} from "@@/shared/providers/provider-id";
import {
    providerIdZodSchema,
} from "@@/shared/providers/provider-id";
import type {
    UserAddress} from "@@/shared/users/user-address";
import {
    userAddressZodSchema,
} from "@@/shared/users/user-address";
import { generateId } from "@@/utilities/generate-id";
import type { Except } from "type-fest";
import { z } from "zod";
import type {
    DeliveryZone} from "../../delivery-zone/delivery-zone";
import {
    deliveryZoneZodSchema,
} from "../../delivery-zone/delivery-zone";

import type { Time } from "@@/shared/date-time/time";
import { timeRangeZodSchema } from "@@/shared/date-time/time-range";
import { timeSpotZodSchema } from "@@/shared/date-time/time-spot";
import { phoneNumberZodSchema } from "@@/shared/phone";
import type { ShippingId} from "../../shipping/shipping-id";
import { shippingIdZodSchema } from "../../shipping/shipping-id";
import type { ShipmentSize} from "../shipment-sizes";
import { shipmentSizes } from "../shipment-sizes";

type ProviderBuyShipmentCommand = {
    readonly _id: string;
    readonly _type: "PROVIDER_BUY_SHIPMENT_COMMAND";
    readonly _createdAt: IsoAndUnixTimestamp;
    readonly shippingId: ShippingId;
    readonly providerId: ProviderId;
    readonly address: UserAddress;
    readonly slot: Time | null;
    readonly price: Price;
    readonly size: ShipmentSize;
    readonly zone: DeliveryZone;

    readonly phone: string;
    readonly name: string;
    readonly reference: string;
};

type FactoryParams = Except<
    ProviderBuyShipmentCommand,
    "_id" | "_type" | "_createdAt"
>;
const providerBuyShipmentCommandFactory = (params: FactoryParams) => {
    const command: ProviderBuyShipmentCommand = {
        ...params,
        _id: generateId({ prefix: "provider_buy_shipment_command__" }),
        _type: "PROVIDER_BUY_SHIPMENT_COMMAND",
        _createdAt: nowIsoAndUnix(),
    };
    return command;
};

const providerBuyShipmentCommandZodSchema: z.ZodSchema<ProviderBuyShipmentCommand> =
    z.object({
        _id: idZodSchema,
        _type: z.literal("PROVIDER_BUY_SHIPMENT_COMMAND"),
        _createdAt: isoAndUnixZodSchema,
        shippingId: shippingIdZodSchema,
        size: z.enum(shipmentSizes),
        zone: deliveryZoneZodSchema,
        price: priceZodSchema,
        slot: z.union([timeSpotZodSchema, timeRangeZodSchema]),
        name: z.string(),
        phone: phoneNumberZodSchema,
        reference: z.string(),
        providerId: providerIdZodSchema,
        address: userAddressZodSchema,
    });

export {
    providerBuyShipmentCommandFactory,
    providerBuyShipmentCommandZodSchema,
};
export type {
    FactoryParams as CreateProviderBuyShipmentCommandParams,
    ProviderBuyShipmentCommand,
};
